import { Icon } from './chunk-SAYUQY3J.js';
export { FILLED_ICON_NAMES, ICON_BUILTIN_DEFAULTS, ICON_WEIGHTS, Icon, IconDefaultsProvider, LIB_VERSION, getIconDefaults, hasFilledVariant, resetIconDefaults, resolveIconDefaults, setIconDefaults, useIconDefaults } from './chunk-SAYUQY3J.js';
export { CURATED_ICONS, CURATED_ICON_NAMES, CURATED_ICON_SLUGS, IconDefaultsSchema, IconNameOrCustomSchema, IconNameSchema, IconPropsSchema, IconShapeSchema, RegisterCustomIconInputSchema, listRegisteredIconNames, registerCustomIcon, resolveIcon, unregisterCustomIcon } from './chunk-CQT6I7B4.js';
import { registerResponsiveContract } from '@norkron/web-conformance';
import { z } from 'zod';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { forwardRef } from 'react';
import { jsx } from 'react/jsx-runtime';

var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-icons",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Fixed-aspect-ratio vector glyph; identical affordance at every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);
var CURATED_ICON_SLUGSSchema = z.record(
  z.string().min(1),
  z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Lucide slugs are lowercase kebab-case")
);
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-icons";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function parseColor(input) {
  if (!input) return null;
  const s = input.trim().toLowerCase();
  if (s === "transparent") return [0, 0, 0, 0];
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].some(Number.isNaN)) return null;
      return [r, g, b, 1];
    }
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      if ([r, g, b].some(Number.isNaN)) return null;
      return [r, g, b, 1];
    }
    return null;
  }
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)$/);
  if (m) {
    return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]), m[4] !== void 0 ? parseFloat(m[4]) : 1];
  }
  return null;
}
function srgbToLinear(channel) {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
function luminance(rgb) {
  return 0.2126 * srgbToLinear(rgb[0]) + 0.7152 * srgbToLinear(rgb[1]) + 0.0722 * srgbToLinear(rgb[2]);
}
function contrastRatio(fg, bg) {
  const l1 = luminance(fg);
  const l2 = luminance(bg);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}
function resolveOpaqueBg(el, win) {
  let cur = el;
  while (cur) {
    const cs = win.getComputedStyle(cur);
    const parsed = parseColor(cs.backgroundColor ?? "");
    if (parsed && parsed[3] > 0.999) return [parsed[0], parsed[1], parsed[2]];
    cur = cur.parentElement;
  }
  return [255, 255, 255];
}
function selectorFor(el) {
  const name = el.getAttribute("data-icon-name");
  if (name) return `svg[data-icon-name="${name}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const icons = Array.from(
      root.querySelectorAll(
        'svg[data-norkron-lib^="@norkron/web-icons"]'
      )
    );
    for (const svg of icons) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "IC_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      const hidden = svg.getAttribute("aria-hidden") === "true";
      if (hidden) continue;
      const hasTitle = svg.querySelector(":scope > title, title") !== null;
      const hasAriaLabel = (svg.getAttribute("aria-label") ?? "").trim().length > 0 || (svg.getAttribute("aria-labelledby") ?? "").trim().length > 0;
      if (!hasTitle && !hasAriaLabel) {
        const name = svg.getAttribute("data-icon-name") ?? "<unknown>";
        findings.push({
          libId: LIB_ID,
          code: "IC_MISSING_TITLE",
          category: "a11y",
          severity: "minor",
          message: `Icon "${name}" rendered without a <title> child or aria-label \u2014 screen-reader users cannot identify it.`,
          selector: selectorFor(svg),
          suggestedFix: 'Pass `title="..."` or `aria-label="..."` to the Icon, or mark `aria-hidden="true"` if purely decorative.',
          extras: { iconName: name }
        });
      }
      try {
        const doc = svg.ownerDocument;
        const win = doc?.defaultView;
        if (win && typeof win.getComputedStyle === "function") {
          const cs = win.getComputedStyle(svg);
          const strokeRaw = cs.stroke || svg.getAttribute("stroke") || cs.color || "";
          const fg = parseColor(strokeRaw);
          if (fg && fg[3] > 0.01) {
            const bg = resolveOpaqueBg(svg, win);
            const ratio = contrastRatio([fg[0], fg[1], fg[2]], bg);
            if (ratio + 1e-6 < 3) {
              const name = svg.getAttribute("data-icon-name") ?? "<unknown>";
              findings.push({
                libId: LIB_ID,
                code: "WEB_ICONS_DEFAULT_TONE_LOW_CONTRAST",
                category: "a11y",
                severity: "major",
                message: `Icon "${name}" resolves to stroke contrast ${ratio.toFixed(2)}:1 vs background \u2014 WCAG SC 1.4.11 graphical-object minimum is 3:1.`,
                selector: selectorFor(svg),
                suggestedFix: 'Pass `color="<dark-token>"` to the Icon, or bump the ancestor `color` CSS to a body-text-strength token so `currentColor` resolves to a usable hue.',
                extras: { iconName: name, ratio: Math.round(ratio * 100) / 100, threshold: 3, fg: [fg[0], fg[1], fg[2]], bg }
              });
            }
          }
        }
      } catch {
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "IC_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
function bind(name) {
  return forwardRef(function Bound(props, ref) {
    return /* @__PURE__ */ jsx(Icon, { ref, name, ...props });
  });
}
var AlarmClockOff = bind("AlarmClockOff");
var Accessibility = bind("Accessibility");
var AlarmClockCheck = bind("AlarmClockCheck");
var AlarmClockMinus = bind("AlarmClockMinus");
var Activity = bind("Activity");
var AlarmClockPlus = bind("AlarmClockPlus");
var AlarmSmoke = bind("AlarmSmoke");
var AlarmClock = bind("AlarmClock");
var AlertCircle = bind("AlertCircle");
var ArchiveX = bind("ArchiveX");
var ArrowBigRight = bind("ArrowBigRight");
var ArrowBigDown = bind("ArrowBigDown");
var AreaChart = bind("AreaChart");
var ArrowBigUp = bind("ArrowBigUp");
var ArrowBigLeft = bind("ArrowBigLeft");
var AlertTriangle = bind("AlertTriangle");
var Angry = bind("Angry");
var Archive = bind("Archive");
var ArchiveRestore = bind("ArchiveRestore");
var Anchor = bind("Anchor");
var Annoyed = bind("Annoyed");
var Apple = bind("Apple");
var ArrowDown = bind("ArrowDown");
var ArrowDownLeft = bind("ArrowDownLeft");
var ArrowLeft = bind("ArrowLeft");
var ArrowDownRight = bind("ArrowDownRight");
var ArrowUpDown = bind("ArrowUpDown");
var ArrowUp = bind("ArrowUp");
var ArrowRight = bind("ArrowRight");
var ArrowLeftRight = bind("ArrowLeftRight");
var ArrowUpRight = bind("ArrowUpRight");
var ArrowUpLeft = bind("ArrowUpLeft");
var Asterisk = bind("Asterisk");
var Atom = bind("Atom");
var Award = bind("Award");
var Banana = bind("Banana");
var BadgeDollarSign = bind("BadgeDollarSign");
var Baby = bind("Baby");
var BarChart = bind("BarChart");
var Banknote = bind("Banknote");
var BarChart2 = bind("BarChart2");
var Barcode = bind("Barcode");
var Baseline = bind("Baseline");
var BarChart3 = bind("BarChart3");
var Battery = bind("Battery");
var BarChart4 = bind("BarChart4");
var BatteryLow = bind("BatteryLow");
var BatteryCharging = bind("BatteryCharging");
var Beer = bind("Beer");
var Beaker = bind("Beaker");
var Bell = bind("Bell");
var BellOff = bind("BellOff");
var BellPlus = bind("BellPlus");
var BellMinus = bind("BellMinus");
var BellDot = bind("BellDot");
var BellRing = bind("BellRing");
var Bike = bind("Bike");
var BikeElectric = bind("BikeElectric");
var Bluetooth = bind("Bluetooth");
var AtSign = bind("AtSign");
var BookCopy = bind("BookCopy");
var BookHeart = bind("BookHeart");
var Book = bind("Book");
var Bold = bind("Bold");
var BookA = bind("BookA");
var BookCheck = bind("BookCheck");
var BookImage = bind("BookImage");
var BookPlus = bind("BookPlus");
var BookOpen = bind("BookOpen");
var BookKey = bind("BookKey");
var BookLock = bind("BookLock");
var BookText = bind("BookText");
var Bookmark2 = bind("Bookmark2");
var BookMarked = bind("BookMarked");
var Bookmark = bind("Bookmark");
var BookmarkCheck = bind("BookmarkCheck");
var BookmarkX = bind("BookmarkX");
var BookmarkPlus = bind("BookmarkPlus");
var Boxes = bind("Boxes");
var BookmarkMinus = bind("BookmarkMinus");
var Box = bind("Box");
var Braces = bind("Braces");
var Brackets = bind("Brackets");
var Bus = bind("Bus");
var Building = bind("Building");
var CalendarClock = bind("CalendarClock");
var CalendarDays = bind("CalendarDays");
var CalendarHeart = bind("CalendarHeart");
var Cable = bind("Cable");
var Building2 = bind("Building2");
var Calendar = bind("Calendar");
var CalendarCheck = bind("CalendarCheck");
var CalendarMinus = bind("CalendarMinus");
var Camera = bind("Camera");
var CameraOff = bind("CameraOff");
var CalendarX = bind("CalendarX");
var CalendarPlus = bind("CalendarPlus");
var CalendarRange = bind("CalendarRange");
var Car = bind("Car");
var CarTaxiFront = bind("CarTaxiFront");
var Castle = bind("Castle");
var Carrot = bind("Carrot");
var ChartCandlestick = bind("ChartCandlestick");
var CaseUpper = bind("CaseUpper");
var CaseLower = bind("CaseLower");
var ChartNoAxesColumn = bind("ChartNoAxesColumn");
var ChartGantt = bind("ChartGantt");
var CaseSensitive = bind("CaseSensitive");
var CarFront = bind("CarFront");
var ChevronDown = bind("ChevronDown");
var CheckCircle = bind("CheckCircle");
var ChevronLast = bind("ChevronLast");
var Cherry = bind("Cherry");
var Check = bind("Check");
var ChevronFirst = bind("ChevronFirst");
var ChevronLeft = bind("ChevronLeft");
var ChevronRight = bind("ChevronRight");
var ChevronsUp = bind("ChevronsUp");
var ChevronsLeftRight = bind("ChevronsLeftRight");
var ChevronsRight = bind("ChevronsRight");
var ChevronsUpDown = bind("ChevronsUpDown");
var ChevronUp = bind("ChevronUp");
var Church = bind("Church");
var Circle = bind("Circle");
var CircleAlert = bind("CircleAlert");
var ChevronsDown = bind("ChevronsDown");
var ChevronsLeft = bind("ChevronsLeft");
var CircleArrowUp = bind("CircleArrowUp");
var CircleArrowRight = bind("CircleArrowRight");
var CircleArrowDown = bind("CircleArrowDown");
var CircleArrowLeft = bind("CircleArrowLeft");
var CircleDot = bind("CircleDot");
var CircleEllipsis = bind("CircleEllipsis");
var CircleSlash = bind("CircleSlash");
var CircleHelp = bind("CircleHelp");
var CircleMinus = bind("CircleMinus");
var CirclePause = bind("CirclePause");
var CirclePlay = bind("CirclePlay");
var CirclePlus = bind("CirclePlus");
var CircleSlash2 = bind("CircleSlash2");
var CircleUser = bind("CircleUser");
var Citrus = bind("Citrus");
var CircleUserRound = bind("CircleUserRound");
var CircleDashed = bind("CircleDashed");
var ClipboardCheck = bind("ClipboardCheck");
var Clipboard = bind("Clipboard");
var Clapperboard = bind("Clapperboard");
var ClipboardCopy = bind("ClipboardCopy");
var Clock10 = bind("Clock10");
var Clock12 = bind("Clock12");
var Clock11 = bind("Clock11");
var Clock = bind("Clock");
var Clock3 = bind("Clock3");
var Clock2 = bind("Clock2");
var ClipboardList = bind("ClipboardList");
var Clock6 = bind("Clock6");
var Clock1 = bind("Clock1");
var ClipboardPaste = bind("ClipboardPaste");
var Clock4 = bind("Clock4");
var Clock5 = bind("Clock5");
var Clock7 = bind("Clock7");
var Clock9 = bind("Clock9");
var Clock8 = bind("Clock8");
var CloudHail = bind("CloudHail");
var CloudAlert = bind("CloudAlert");
var CloudMoonRain = bind("CloudMoonRain");
var CloudCheck = bind("CloudCheck");
var CloudDownload = bind("CloudDownload");
var Cloud = bind("Cloud");
var CloudFog = bind("CloudFog");
var CloudLightning = bind("CloudLightning");
var CloudMoon = bind("CloudMoon");
var CloudDrizzle = bind("CloudDrizzle");
var CloudRain = bind("CloudRain");
var CloudCog = bind("CloudCog");
var CloudOff = bind("CloudOff");
var CloudSnow = bind("CloudSnow");
var CloudSunRain = bind("CloudSunRain");
var CloudSun = bind("CloudSun");
var CloudUpload = bind("CloudUpload");
var Code = bind("Code");
var Code2 = bind("Code2");
var Columns4 = bind("Columns4");
var Columns2 = bind("Columns2");
var ContactRound = bind("ContactRound");
var CodeXml = bind("CodeXml");
var Coffee = bind("Coffee");
var Command = bind("Command");
var Coins = bind("Coins");
var Contact = bind("Contact");
var Container = bind("Container");
var Columns3 = bind("Columns3");
var Compass = bind("Compass");
var Copy = bind("Copy");
var Cookie = bind("Cookie");
var CornerDownLeft = bind("CornerDownLeft");
var CornerDownRight = bind("CornerDownRight");
var CornerLeftUp = bind("CornerLeftUp");
var Crown2 = bind("Crown2");
var CreditCard = bind("CreditCard");
var CornerRightUp = bind("CornerRightUp");
var Crosshair = bind("Crosshair");
var Croissant = bind("Croissant");
var CornerUpRight = bind("CornerUpRight");
var Crown = bind("Crown");
var CupSoda = bind("CupSoda");
var Crop = bind("Crop");
var Cpu = bind("Cpu");
var Database = bind("Database");
var CornerUpLeft = bind("CornerUpLeft");
var Dice1 = bind("Dice1");
var Diamond = bind("Diamond");
var Dice2 = bind("Dice2");
var Dice3 = bind("Dice3");
var Dice4 = bind("Dice4");
var Dices = bind("Dices");
var DollarSign = bind("DollarSign");
var Dice5 = bind("Dice5");
var Dice6 = bind("Dice6");
var Dot = bind("Dot");
var Disc2 = bind("Disc2");
var Donut = bind("Donut");
var Disc = bind("Disc");
var Download = bind("Download");
var Disc3 = bind("Disc3");
var Drill = bind("Drill");
var Droplet = bind("Droplet");
var Droplets = bind("Droplets");
var Earth = bind("Earth");
var Edit = bind("Edit");
var Eraser = bind("Eraser");
var ExternalLink = bind("ExternalLink");
var EyeOff = bind("EyeOff");
var FileBox = bind("FileBox");
var FastForward = bind("FastForward");
var Factory = bind("Factory");
var FileCheck = bind("FileCheck");
var Eye = bind("Eye");
var FileArchive = bind("FileArchive");
var File = bind("File");
var EyeClosed = bind("EyeClosed");
var FileCode = bind("FileCode");
var FileCsv = bind("FileCsv");
var FileImage = bind("FileImage");
var FileLock = bind("FileLock");
var FileDown = bind("FileDown");
var FileMinus = bind("FileMinus");
var FileSymlink = bind("FileSymlink");
var FileText = bind("FileText");
var Files = bind("Files");
var Film = bind("Film");
var FilePen = bind("FilePen");
var FilePlus = bind("FilePlus");
var FileSearch = bind("FileSearch");
var FileX = bind("FileX");
var Filter = bind("Filter");
var FileUp = bind("FileUp");
var Flag = bind("Flag");
var Flame = bind("Flame");
var Folder = bind("Folder");
var FlaskRound = bind("FlaskRound");
var FlaskConical = bind("FlaskConical");
var FolderInput = bind("FolderInput");
var FolderDown = bind("FolderDown");
var FolderOutput = bind("FolderOutput");
var FolderGit = bind("FolderGit");
var FolderLock = bind("FolderLock");
var FolderKanban = bind("FolderKanban");
var FolderOpen = bind("FolderOpen");
var FolderMinus = bind("FolderMinus");
var FolderCheck = bind("FolderCheck");
var FolderGit2 = bind("FolderGit2");
var FolderArchive = bind("FolderArchive");
var FolderSearch = bind("FolderSearch");
var FolderPlus = bind("FolderPlus");
var FolderX = bind("FolderX");
var FolderUp = bind("FolderUp");
var FolderTree = bind("FolderTree");
var Gift = bind("Gift");
var Gem = bind("Gem");
var GitBranchPlus = bind("GitBranchPlus");
var GitBranch = bind("GitBranch");
var GitCommitHorizontal = bind("GitCommitHorizontal");
var Gamepad2 = bind("Gamepad2");
var Gamepad = bind("Gamepad");
var Forward = bind("Forward");
var Frown = bind("Frown");
var GitFork = bind("GitFork");
var GitCommitVertical = bind("GitCommitVertical");
var GitGraph = bind("GitGraph");
var GitCompare = bind("GitCompare");
var GitMerge = bind("GitMerge");
var GitPullRequest = bind("GitPullRequest");
var GitPullRequestArrow = bind("GitPullRequestArrow");
var GitPullRequestClosed = bind("GitPullRequestClosed");
var Glasses = bind("Glasses");
var Grape = bind("Grape");
var Globe = bind("Globe");
var Hammer = bind("Hammer");
var HandCoins = bind("HandCoins");
var HardDrive = bind("HardDrive");
var Heading = bind("Heading");
var Hash = bind("Hash");
var Heading1 = bind("Heading1");
var Heading2 = bind("Heading2");
var Heading3 = bind("Heading3");
var Headphones = bind("Headphones");
var Heading5 = bind("Heading5");
var Heading6 = bind("Heading6");
var Heading4 = bind("Heading4");
var HelpCircle = bind("HelpCircle");
var Heart = bind("Heart");
var Home = bind("Home");
var Hexagon = bind("Hexagon");
var Highlighter = bind("Highlighter");
var IdCard = bind("IdCard");
var Hospital = bind("Hospital");
var Hourglass = bind("Hourglass");
var IceCream = bind("IceCream");
var Image = bind("Image");
var IceCreamBowl = bind("IceCreamBowl");
var Hotel = bind("Hotel");
var Info = bind("Info");
var Italic = bind("Italic");
var Joystick = bind("Joystick");
var Inbox = bind("Inbox");
var Key = bind("Key");
var KeySquare = bind("KeySquare");
var KeyRound = bind("KeyRound");
var Landmark = bind("Landmark");
var Languages = bind("Languages");
var Laptop = bind("Laptop");
var LayoutGrid = bind("LayoutGrid");
var Laugh = bind("Laugh");
var LayoutList = bind("LayoutList");
var Layers = bind("Layers");
var Layers2 = bind("Layers2");
var LayoutDashboard = bind("LayoutDashboard");
var LayoutPanelTop = bind("LayoutPanelTop");
var LayoutPanelLeft = bind("LayoutPanelLeft");
var List = bind("List");
var ListChecks = bind("ListChecks");
var LightbulbOff = bind("LightbulbOff");
var Leaf = bind("Leaf");
var LayoutTemplate = bind("LayoutTemplate");
var Link = bind("Link");
var Lightbulb = bind("Lightbulb");
var LineChart = bind("LineChart");
var ListOrdered = bind("ListOrdered");
var Library = bind("Library");
var LockKeyhole = bind("LockKeyhole");
var Loader = bind("Loader");
var Lock = bind("Lock");
var ListUnordered = bind("ListUnordered");
var LockKeyholeOpen = bind("LockKeyholeOpen");
var MailSearch = bind("MailSearch");
var Mail = bind("Mail");
var MailPlus = bind("MailPlus");
var Magnet = bind("Magnet");
var MailX = bind("MailX");
var LogIn = bind("LogIn");
var MailCheck = bind("MailCheck");
var MailMinus = bind("MailMinus");
var LogOut = bind("LogOut");
var Mails = bind("Mails");
var MailWarning = bind("MailWarning");
var MailOpen = bind("MailOpen");
var Martini = bind("Martini");
var Map = bind("Map");
var MapPin = bind("MapPin");
var Maximize = bind("Maximize");
var Meh = bind("Meh");
var MegaphoneOff = bind("MegaphoneOff");
var Medal = bind("Medal");
var Megaphone = bind("Megaphone");
var Menu = bind("Menu");
var MemoryStick = bind("MemoryStick");
var MessageCircleHeart = bind("MessageCircleHeart");
var MessageCircle = bind("MessageCircle");
var MessageCirclePlus = bind("MessageCirclePlus");
var MessageSquareX = bind("MessageSquareX");
var Mic = bind("Mic");
var MessagesSquare = bind("MessagesSquare");
var MessageCircleQuestion = bind("MessageCircleQuestion");
var MessageSquare = bind("MessageSquare");
var MessageSquarePlus = bind("MessageSquarePlus");
var MessageCircleX = bind("MessageCircleX");
var MicOff = bind("MicOff");
var Minimize = bind("Minimize");
var Microscope = bind("Microscope");
var Minus = bind("Minus");
var MoreVertical = bind("MoreVertical");
var Moon = bind("Moon");
var MoreHorizontal = bind("MoreHorizontal");
var Monitor = bind("Monitor");
var Mountain = bind("Mountain");
var MoveHorizontal = bind("MoveHorizontal");
var MoveDown = bind("MoveDown");
var MoveRight = bind("MoveRight");
var MoveLeft = bind("MoveLeft");
var MoveDiagonal = bind("MoveDiagonal");
var Move = bind("Move");
var MountainSnow = bind("MountainSnow");
var MoveUp = bind("MoveUp");
var Music = bind("Music");
var Network = bind("Network");
var Navigation = bind("Navigation");
var NotebookPen = bind("NotebookPen");
var Notebook = bind("Notebook");
var MoveVertical = bind("MoveVertical");
var NotebookText = bind("NotebookText");
var Newspaper = bind("Newspaper");
var Octagon = bind("Octagon");
var PackageMinus = bind("PackageMinus");
var PackageCheck = bind("PackageCheck");
var Package = bind("Package");
var PackagePlus = bind("PackagePlus");
var PackageOpen = bind("PackageOpen");
var Package2 = bind("Package2");
var PackageX = bind("PackageX");
var Paintbrush2 = bind("Paintbrush2");
var PaintBucket = bind("PaintBucket");
var Paintbrush = bind("Paintbrush");
var Palette = bind("Palette");
var Pen = bind("Pen");
var Paperclip = bind("Paperclip");
var PartyPopper = bind("PartyPopper");
var Pause = bind("Pause");
var PenTool = bind("PenTool");
var Pencil = bind("Pencil");
var PencilLine = bind("PencilLine");
var Percent = bind("Percent");
var PenLine = bind("PenLine");
var PersonStanding = bind("PersonStanding");
var PencilRuler = bind("PencilRuler");
var Pentagon = bind("Pentagon");
var Phone = bind("Phone");
var PhoneCall = bind("PhoneCall");
var PhoneMissed = bind("PhoneMissed");
var PiggyBank = bind("PiggyBank");
var PieChart = bind("PieChart");
var PhoneOff = bind("PhoneOff");
var PhoneOutgoing = bind("PhoneOutgoing");
var PhoneIncoming = bind("PhoneIncoming");
var Pipette = bind("Pipette");
var PlaneLanding = bind("PlaneLanding");
var Plane = bind("Plane");
var Pizza = bind("Pizza");
var Pilcrow = bind("Pilcrow");
var Pin = bind("Pin");
var PlaneTakeoff = bind("PlaneTakeoff");
var Play = bind("Play");
var Plus = bind("Plus");
var Plus2 = bind("Plus2");
var Power = bind("Power");
var PlugZap = bind("PlugZap");
var Plug = bind("Plug");
var PowerOff = bind("PowerOff");
var Puzzle = bind("Puzzle");
var Printer = bind("Printer");
var Quote = bind("Quote");
var RefreshCw = bind("RefreshCw");
var Radio = bind("Radio");
var QrCode = bind("QrCode");
var Receipt = bind("Receipt");
var Rainbow = bind("Rainbow");
var Redo = bind("Redo");
var RemoveFormatting = bind("RemoveFormatting");
var ReplyAll = bind("ReplyAll");
var Rewind = bind("Rewind");
var Reply = bind("Reply");
var Repeat2 = bind("Repeat2");
var Rocket = bind("Rocket");
var Repeat = bind("Repeat");
var RotateCcw = bind("RotateCcw");
var RotateCw = bind("RotateCw");
var Rows3 = bind("Rows3");
var Sailboat = bind("Sailboat");
var Ruler = bind("Ruler");
var Sandwich = bind("Sandwich");
var Rows4 = bind("Rows4");
var Rows2 = bind("Rows2");
var Salad = bind("Salad");
var Save = bind("Save");
var ScanBarcode = bind("ScanBarcode");
var ScanLine = bind("ScanLine");
var ScanText = bind("ScanText");
var ScanFace = bind("ScanFace");
var ScanQrCode = bind("ScanQrCode");
var ScanEye = bind("ScanEye");
var ScatterChart = bind("ScatterChart");
var Scan = bind("Scan");
var School = bind("School");
var Scissors = bind("Scissors");
var ServerCrash = bind("ServerCrash");
var Server = bind("Server");
var Send = bind("Send");
var Share = bind("Share");
var Settings = bind("Settings");
var ServerCog = bind("ServerCog");
var ServerOff = bind("ServerOff");
var Servers = bind("Servers");
var Search = bind("Search");
var ShieldAlert = bind("ShieldAlert");
var Share2 = bind("Share2");
var Shield = bind("Shield");
var ShieldOff = bind("ShieldOff");
var ShieldCheck = bind("ShieldCheck");
var ShieldPlus = bind("ShieldPlus");
var ShieldQuestion = bind("ShieldQuestion");
var ShoppingBag = bind("ShoppingBag");
var Shirt = bind("Shirt");
var Ship = bind("Ship");
var ShieldX = bind("ShieldX");
var ShipWheel = bind("ShipWheel");
var ShoppingCart = bind("ShoppingCart");
var ShoppingBasket = bind("ShoppingBasket");
var Shuffle = bind("Shuffle");
var Slash = bind("Slash");
var Sliders = bind("Sliders");
var Smile = bind("Smile");
var SkipForward = bind("SkipForward");
var SmilePlus = bind("SmilePlus");
var SkipBack = bind("SkipBack");
var Smartphone = bind("Smartphone");
var Snowflake = bind("Snowflake");
var Sparkle = bind("Sparkle");
var Soup = bind("Soup");
var Sparkles = bind("Sparkles");
var SquareCode = bind("SquareCode");
var SquareCheck = bind("SquareCheck");
var Spellcheck = bind("Spellcheck");
var Square = bind("Square");
var Spinner = bind("Spinner");
var Spline = bind("Spline");
var Speaker = bind("Speaker");
var Speech = bind("Speech");
var SquareMinus = bind("SquareMinus");
var SquareSlash = bind("SquareSlash");
var SquarePlus = bind("SquarePlus");
var SquareDot = bind("SquareDot");
var SquareDashed = bind("SquareDashed");
var Squircle = bind("Squircle");
var SquareX = bind("SquareX");
var Star = bind("Star");
var StopCircle = bind("StopCircle");
var Subscript = bind("Subscript");
var Superscript = bind("Superscript");
var Store = bind("Store");
var Strikethrough = bind("Strikethrough");
var Sunrise = bind("Sunrise");
var Sunset = bind("Sunset");
var Sun = bind("Sun");
var Sword = bind("Sword");
var Swords = bind("Swords");
var Tags = bind("Tags");
var Tag = bind("Tag");
var Tent = bind("Tent");
var Target = bind("Target");
var Tablet = bind("Tablet");
var Telescope = bind("Telescope");
var TentTree = bind("TentTree");
var Terminal = bind("Terminal");
var TestTube = bind("TestTube");
var TextCursorInput = bind("TextCursorInput");
var ThermometerSnowflake = bind("ThermometerSnowflake");
var TextCursor = bind("TextCursor");
var TestTubes = bind("TestTubes");
var ThumbsUp = bind("ThumbsUp");
var ThumbsDown = bind("ThumbsDown");
var Ticket = bind("Ticket");
var Timer = bind("Timer");
var Thermometer = bind("Thermometer");
var ThermometerSun = bind("ThermometerSun");
var TimerOff = bind("TimerOff");
var TimerReset = bind("TimerReset");
var ToggleLeft = bind("ToggleLeft");
var ToggleRight = bind("ToggleRight");
var Tornado = bind("Tornado");
var Train = bind("Train");
var TrainTrack = bind("TrainTrack");
var TreeDeciduous = bind("TreeDeciduous");
var Trash = bind("Trash");
var Trees = bind("Trees");
var TreePine = bind("TreePine");
var TrendingUpDown = bind("TrendingUpDown");
var Triangle = bind("Triangle");
var TrendingUp = bind("TrendingUp");
var TrendingDown = bind("TrendingDown");
var Tv = bind("Tv");
var Trophy = bind("Trophy");
var Truck = bind("Truck");
var Underline = bind("Underline");
var Umbrella = bind("Umbrella");
var Undo = bind("Undo");
var Type = bind("Type");
var Unlink = bind("Unlink");
var Unlock = bind("Unlock");
var UnlockKeyhole = bind("UnlockKeyhole");
var Upload = bind("Upload");
var User = bind("User");
var UserCheck = bind("UserCheck");
var UserCog = bind("UserCog");
var UserPen = bind("UserPen");
var UserLock = bind("UserLock");
var UserRoundMinus = bind("UserRoundMinus");
var UserRound = bind("UserRound");
var UserRoundCheck = bind("UserRoundCheck");
var UserPlus = bind("UserPlus");
var UserRoundPlus = bind("UserRoundPlus");
var UserMinus = bind("UserMinus");
var UserRoundSearch = bind("UserRoundSearch");
var UserRoundX = bind("UserRoundX");
var UserSearch = bind("UserSearch");
var UserStar = bind("UserStar");
var UserX = bind("UserX");
var Users = bind("Users");
var VolumeOff = bind("VolumeOff");
var UsersRound = bind("UsersRound");
var Volume1 = bind("Volume1");
var VolumeX = bind("VolumeX");
var Volume2 = bind("Volume2");
var Wallet = bind("Wallet");
var UtensilsCrossed = bind("UtensilsCrossed");
var VenetianMask = bind("VenetianMask");
var Video = bind("Video");
var Warehouse = bind("Warehouse");
var Utensils = bind("Utensils");
var Volume = bind("Volume");
var WifiOff = bind("WifiOff");
var Webhook = bind("Webhook");
var Wifi = bind("Wifi");
var Wind = bind("Wind");
var Wine = bind("Wine");
var X = bind("X");
var Workflow = bind("Workflow");
var ZoomIn = bind("ZoomIn");
var ZoomOut = bind("ZoomOut");
var Wrench = bind("Wrench");
var XCircle = bind("XCircle");
var AlignLeft = bind("AlignLeft");
var AlignCenter = bind("AlignCenter");
var AlignRight = bind("AlignRight");
var AlignJustify = bind("AlignJustify");
var IndentIncrease = bind("IndentIncrease");
var IndentDecrease = bind("IndentDecrease");
var Table = bind("Table");
var Table2 = bind("Table2");
var BetweenHorizontalStart = bind("BetweenHorizontalStart");
var BetweenHorizontalEnd = bind("BetweenHorizontalEnd");
var BetweenVerticalStart = bind("BetweenVerticalStart");
var BetweenVerticalEnd = bind("BetweenVerticalEnd");
var TableCellsMerge = bind("TableCellsMerge");
var TableCellsSplit = bind("TableCellsSplit");
var TableColumnsSplit = bind("TableColumnsSplit");
var TableRowsSplit = bind("TableRowsSplit");
var Link2 = bind("Link2");
var Undo2 = bind("Undo2");
var Redo2 = bind("Redo2");
var ListTree = bind("ListTree");
var Scale = bind("Scale");
var Images = bind("Images");
var CheckCircle2 = bind("CheckCircle2");
var CircleCheckBig = bind("CircleCheckBig");
var FolderClosed = bind("FolderClosed");
var Grip = bind("Grip");
var GripHorizontal = bind("GripHorizontal");
var GripVertical = bind("GripVertical");
var History = bind("History");
var LibraryBig = bind("LibraryBig");
var Loader2 = bind("Loader2");
var LoaderCircle = bind("LoaderCircle");
var RotateCcwClock = bind("RotateCcwClock");
var SlidersHorizontal = bind("SlidersHorizontal");
var SlidersVertical = bind("SlidersVertical");
var Trash2 = bind("Trash2");
var Replace = bind("Replace");

// src/index.ts
try {
  registerLazy(
    "@norkron/web-icons",
    () => selfCheck
  );
} catch {
}

export { Accessibility, Activity, AlarmClock, AlarmClockCheck, AlarmClockMinus, AlarmClockOff, AlarmClockPlus, AlarmSmoke, AlertCircle, AlertTriangle, AlignCenter, AlignJustify, AlignLeft, AlignRight, Anchor, Angry, Annoyed, Apple, Archive, ArchiveRestore, ArchiveX, AreaChart, ArrowBigDown, ArrowBigLeft, ArrowBigRight, ArrowBigUp, ArrowDown, ArrowDownLeft, ArrowDownRight, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUp, ArrowUpDown, ArrowUpLeft, ArrowUpRight, Asterisk, AtSign, Atom, Award, Baby, BadgeDollarSign, Banana, Banknote, BarChart, BarChart2, BarChart3, BarChart4, Barcode, Baseline, Battery, BatteryCharging, BatteryLow, Beaker, Beer, Bell, BellDot, BellMinus, BellOff, BellPlus, BellRing, BetweenHorizontalEnd, BetweenHorizontalStart, BetweenVerticalEnd, BetweenVerticalStart, Bike, BikeElectric, Bluetooth, Bold, Book, BookA, BookCheck, BookCopy, BookHeart, BookImage, BookKey, BookLock, BookMarked, BookOpen, BookPlus, BookText, Bookmark, Bookmark2, BookmarkCheck, BookmarkMinus, BookmarkPlus, BookmarkX, Box, Boxes, Braces, Brackets, Building, Building2, Bus, CURATED_ICON_SLUGSSchema, Cable, Calendar, CalendarCheck, CalendarClock, CalendarDays, CalendarHeart, CalendarMinus, CalendarPlus, CalendarRange, CalendarX, Camera, CameraOff, Car, CarFront, CarTaxiFront, Carrot, CaseLower, CaseSensitive, CaseUpper, Castle, ChartCandlestick, ChartGantt, ChartNoAxesColumn, Check, CheckCircle, CheckCircle2, Cherry, ChevronDown, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronUp, ChevronsDown, ChevronsLeft, ChevronsLeftRight, ChevronsRight, ChevronsUp, ChevronsUpDown, Church, Circle, CircleAlert, CircleArrowDown, CircleArrowLeft, CircleArrowRight, CircleArrowUp, CircleCheckBig, CircleDashed, CircleDot, CircleEllipsis, CircleHelp, CircleMinus, CirclePause, CirclePlay, CirclePlus, CircleSlash, CircleSlash2, CircleUser, CircleUserRound, Citrus, Clapperboard, Clipboard, ClipboardCheck, ClipboardCopy, ClipboardList, ClipboardPaste, Clock, Clock1, Clock10, Clock11, Clock12, Clock2, Clock3, Clock4, Clock5, Clock6, Clock7, Clock8, Clock9, Cloud, CloudAlert, CloudCheck, CloudCog, CloudDownload, CloudDrizzle, CloudFog, CloudHail, CloudLightning, CloudMoon, CloudMoonRain, CloudOff, CloudRain, CloudSnow, CloudSun, CloudSunRain, CloudUpload, Code, Code2, CodeXml, Coffee, Coins, Columns2, Columns3, Columns4, Command, Compass, Contact, ContactRound, Container, Cookie, Copy, CornerDownLeft, CornerDownRight, CornerLeftUp, CornerRightUp, CornerUpLeft, CornerUpRight, Cpu, CreditCard, Croissant, Crop, Crosshair, Crown, Crown2, CupSoda, Database, Diamond, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Dices, Disc, Disc2, Disc3, DollarSign, Donut, Dot, Download, Drill, Droplet, Droplets, Earth, Edit, Eraser, ExternalLink, Eye, EyeClosed, EyeOff, Factory, FastForward, File, FileArchive, FileBox, FileCheck, FileCode, FileCsv, FileDown, FileImage, FileLock, FileMinus, FilePen, FilePlus, FileSearch, FileSymlink, FileText, FileUp, FileX, Files, Film, Filter, Flag, Flame, FlaskConical, FlaskRound, Folder, FolderArchive, FolderCheck, FolderClosed, FolderDown, FolderGit, FolderGit2, FolderInput, FolderKanban, FolderLock, FolderMinus, FolderOpen, FolderOutput, FolderPlus, FolderSearch, FolderTree, FolderUp, FolderX, Forward, Frown, Gamepad, Gamepad2, Gem, Gift, GitBranch, GitBranchPlus, GitCommitHorizontal, GitCommitVertical, GitCompare, GitFork, GitGraph, GitMerge, GitPullRequest, GitPullRequestArrow, GitPullRequestClosed, Glasses, Globe, Grape, Grip, GripHorizontal, GripVertical, Hammer, HandCoins, HardDrive, Hash, Heading, Heading1, Heading2, Heading3, Heading4, Heading5, Heading6, Headphones, Heart, HelpCircle, Hexagon, Highlighter, History, Home, Hospital, Hotel, Hourglass, IceCream, IceCreamBowl, IdCard, Image, Images, Inbox, IndentDecrease, IndentIncrease, Info, Italic, Joystick, Key, KeyRound, KeySquare, Landmark, Languages, Laptop, Laugh, Layers, Layers2, LayoutDashboard, LayoutGrid, LayoutList, LayoutPanelLeft, LayoutPanelTop, LayoutTemplate, Leaf, Library, LibraryBig, Lightbulb, LightbulbOff, LineChart, Link, Link2, List, ListChecks, ListOrdered, ListTree, ListUnordered, Loader, Loader2, LoaderCircle, Lock, LockKeyhole, LockKeyholeOpen, LogIn, LogOut, Magnet, Mail, MailCheck, MailMinus, MailOpen, MailPlus, MailSearch, MailWarning, MailX, Mails, Map, MapPin, Martini, Maximize, Medal, Megaphone, MegaphoneOff, Meh, MemoryStick, Menu, MessageCircle, MessageCircleHeart, MessageCirclePlus, MessageCircleQuestion, MessageCircleX, MessageSquare, MessageSquarePlus, MessageSquareX, MessagesSquare, Mic, MicOff, Microscope, Minimize, Minus, Monitor, Moon, MoreHorizontal, MoreVertical, Mountain, MountainSnow, Move, MoveDiagonal, MoveDown, MoveHorizontal, MoveLeft, MoveRight, MoveUp, MoveVertical, Music, Navigation, Network, Newspaper, Notebook, NotebookPen, NotebookText, Octagon, Package, Package2, PackageCheck, PackageMinus, PackageOpen, PackagePlus, PackageX, PaintBucket, Paintbrush, Paintbrush2, Palette, Paperclip, PartyPopper, Pause, Pen, PenLine, PenTool, Pencil, PencilLine, PencilRuler, Pentagon, Percent, PersonStanding, Phone, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, PieChart, PiggyBank, Pilcrow, Pin, Pipette, Pizza, Plane, PlaneLanding, PlaneTakeoff, Play, Plug, PlugZap, Plus, Plus2, Power, PowerOff, Printer, Puzzle, QrCode, Quote, RESPONSIVE_CONTRACT, Radio, Rainbow, Receipt, Redo, Redo2, RefreshCw, RemoveFormatting, Repeat, Repeat2, Replace, Reply, ReplyAll, Rewind, Rocket, RotateCcw, RotateCcwClock, RotateCw, Rows2, Rows3, Rows4, Ruler, Sailboat, Salad, Sandwich, Save, Scale, Scan, ScanBarcode, ScanEye, ScanFace, ScanLine, ScanQrCode, ScanText, ScatterChart, School, Scissors, Search, Send, Server, ServerCog, ServerCrash, ServerOff, Servers, Settings, Share, Share2, Shield, ShieldAlert, ShieldCheck, ShieldOff, ShieldPlus, ShieldQuestion, ShieldX, Ship, ShipWheel, Shirt, ShoppingBag, ShoppingBasket, ShoppingCart, Shuffle, SkipBack, SkipForward, Slash, Sliders, SlidersHorizontal, SlidersVertical, Smartphone, Smile, SmilePlus, Snowflake, Soup, Sparkle, Sparkles, Speaker, Speech, Spellcheck, Spinner, Spline, Square, SquareCheck, SquareCode, SquareDashed, SquareDot, SquareMinus, SquarePlus, SquareSlash, SquareX, Squircle, Star, StopCircle, Store, Strikethrough, Subscript, Sun, Sunrise, Sunset, Superscript, Sword, Swords, Table, Table2, TableCellsMerge, TableCellsSplit, TableColumnsSplit, TableRowsSplit, Tablet, Tag, Tags, Target, Telescope, Tent, TentTree, Terminal, TestTube, TestTubes, TextCursor, TextCursorInput, Thermometer, ThermometerSnowflake, ThermometerSun, ThumbsDown, ThumbsUp, Ticket, Timer, TimerOff, TimerReset, ToggleLeft, ToggleRight, Tornado, Train, TrainTrack, Trash, Trash2, TreeDeciduous, TreePine, Trees, TrendingDown, TrendingUp, TrendingUpDown, Triangle, Trophy, Truck, Tv, Type, Umbrella, Underline, Undo, Undo2, Unlink, Unlock, UnlockKeyhole, Upload, User, UserCheck, UserCog, UserLock, UserMinus, UserPen, UserPlus, UserRound, UserRoundCheck, UserRoundMinus, UserRoundPlus, UserRoundSearch, UserRoundX, UserSearch, UserStar, UserX, Users, UsersRound, Utensils, UtensilsCrossed, VenetianMask, Video, Volume, Volume1, Volume2, VolumeOff, VolumeX, Wallet, Warehouse, Webhook, Wifi, WifiOff, Wind, Wine, Workflow, Wrench, X, XCircle, ZoomIn, ZoomOut, selfCheck };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map